// Pure logic behind the recipe card: how much is being made, the factor that
// follows from it, and the scaled ingredient list. No React, no network —
// covered by recipeScale.test.ts.
//
// Scaling is linear by design (owner decision, v1): ten times the batch is ten
// times every ingredient. The card's quantities are for card.base_qty of
// card.base_unit, so the factor is always "amount being made ÷ base amount".

import { convertUnits } from '@/lib/inventory-units'
import type { RecipeCard, RecipeCardIngredient, RecipeCardTarget } from './recipeCardTypes'

/**
 * What the amount field is counting.
 *   'base'   portions of a dish, or the prep item's own unit (kg of gravy)
 *   'weight' a dish by weight or volume — only when the recipe states what it makes
 */
export type ScaleMode = 'base' | 'weight'

/** The quick chips: multiples of the recipe as it was written. */
export const SCALE_CHIPS = [1, 2, 5, 10] as const

/** Above this the number is a typo, not a batch. */
export const MAX_AMOUNT = 100_000

export function canScaleByWeight(card: RecipeCard): boolean {
  return card.kind === 'dish' && card.portion_qty != null && card.portion_qty > 0 && card.portion_unit !== ''
}

/** The unit the amount field is in. */
export function amountUnit(card: RecipeCard, mode: ScaleMode): string {
  return mode === 'weight' && canScaleByWeight(card) ? card.portion_unit : card.base_unit
}

function round5(n: number): number {
  return Math.round(n * 1e5) / 1e5
}

/** The recipe as written, in the amount field's unit. */
export function writtenAmount(card: RecipeCard, mode: ScaleMode): number {
  const written = card.written_qty > 0 ? card.written_qty : card.base_qty
  if (mode === 'weight' && canScaleByWeight(card)) return round5(written * (card.portion_qty as number))
  return round5(written)
}

/**
 * How many times the card's base amount is being made. 0 when the amount is
 * not a usable number — the caller shows the recipe unscaled and says why.
 */
export function factorFor(card: RecipeCard, amount: number, mode: ScaleMode): number {
  if (!Number.isFinite(amount) || amount <= 0 || amount > MAX_AMOUNT) return 0
  if (mode === 'weight' && canScaleByWeight(card)) return amount / (card.portion_qty as number) / card.base_qty
  if (!(card.base_qty > 0)) return 0
  return amount / card.base_qty
}

/** The same batch, counted the other way — so switching mode keeps the batch. */
export function amountInMode(card: RecipeCard, amount: number, from: ScaleMode, to: ScaleMode): number {
  if (from === to || !canScaleByWeight(card)) return amount
  const f = factorFor(card, amount, from)
  if (f <= 0) return amount
  const portions = f * card.base_qty
  return to === 'weight' ? round5(portions * (card.portion_qty as number)) : round5(portions)
}

/** The amount a chip stands for: that many times the recipe as written. */
export function chipAmount(card: RecipeCard, mode: ScaleMode, multiple: number): number {
  return round5(writtenAmount(card, mode) * multiple)
}

/**
 * The amount the card opens at, always counted in 'base':
 * the order line's quantity, else what the screen asked for, else the recipe
 * as written.
 */
export function initialAmount(card: RecipeCard, target: RecipeCardTarget | null | undefined): number {
  if (card.line_qty != null && card.line_qty > 0) return card.line_qty
  const asked = target && 'qty' in target ? target.qty : undefined
  if (asked != null && Number.isFinite(asked) && asked > 0) return round5(asked)
  return writtenAmount(card, 'base')
}

/** Reads the amount field. null = not a usable amount. */
export function parseAmount(text: string): number | null {
  const t = text.trim().replace(',', '.')
  if (t === '' || !/^\d*\.?\d+$|^\d+\.$/.test(t)) return null
  const n = Number(t)
  if (!Number.isFinite(n) || n <= 0 || n > MAX_AMOUNT) return null
  return n
}

// ── Units a cook can read ───────────────────────────────────────────────────

const LARGER: Record<string, string> = { g: 'kg', ml: 'liter' }
const SMALLER: Record<string, string> = { kg: 'g', liter: 'ml' }

const UNIT_SHORT: Record<string, string> = { liter: 'L', each: 'pc', portion: 'portion' }

/** How the unit is written on the card ("L", "pc", "kg"). */
export function unitLabel(unit: string, qty = 1): string {
  if (unit === 'portion') return qty === 1 ? 'portion' : 'portions'
  if (unit === 'each') return qty === 1 ? 'pc' : 'pcs'
  return UNIT_SHORT[unit] ?? unit
}

/** Decimals that make sense on a kitchen scale, per unit. */
function decimalsFor(unit: string): number {
  if (unit === 'kg' || unit === 'liter' || unit === 'lb') return 3
  if (unit === 'g' || unit === 'ml' || unit === 'oz') return 1
  return 2
}

export interface ReadableQty {
  qty: number
  unit: string
}

/**
 * 1500 g reads as 1.5 kg and 0.25 kg as 250 g. Units outside the metric
 * weight and volume families (each, dozen, oz, pack, box …) are left in the
 * unit the recipe was written in.
 */
export function readableQty(qty: number, unit: string): ReadableQty {
  let q = qty
  let u = unit
  if (LARGER[u] && Math.abs(q) >= 1000) {
    q = q / 1000
    u = LARGER[u]
  } else if (SMALLER[u] && Math.abs(q) > 0 && Math.abs(q) < 1) {
    q = q * 1000
    u = SMALLER[u]
  }
  const p = 10 ** decimalsFor(u)
  let rounded = Math.round(q * p) / p
  // Never show a real ingredient as zero: keep enough decimals to see it.
  if (rounded === 0 && q > 0) rounded = round5(q)
  return { qty: rounded, unit: u }
}

/** "1.5 kg", "250 g", "12 pcs". */
export function formatQty(qty: number, unit: string): string {
  const r = readableQty(qty, unit)
  const text = Number.isInteger(r.qty) ? String(r.qty) : String(round5(r.qty))
  return `${text} ${unitLabel(r.unit, r.qty)}`
}

export interface ScaledIngredient {
  stock_item_id: string
  name: string
  /** The scaled amount, already in a unit a cook can read. */
  qty: number
  unit: string
  text: string
  has_card: boolean
  from: string[]
  /** How much of this prep item to make, in its own unit — opens its card. */
  prepQty: number
  prepUnit: string
}

export function scaleIngredient(i: RecipeCardIngredient, factor: number): ScaledIngredient {
  const f = factor > 0 ? factor : 1
  const r = readableQty(i.qty * f, i.unit)
  return {
    stock_item_id: i.stock_item_id,
    name: i.name,
    qty: r.qty,
    unit: r.unit,
    text: formatQty(i.qty * f, i.unit),
    has_card: i.has_card,
    from: i.from ?? [],
    prepQty: round5(i.stock_qty * f),
    prepUnit: i.stock_unit,
  }
}

export function scaleIngredients(card: RecipeCard, factor: number): ScaledIngredient[] {
  return card.ingredients.map((i) => scaleIngredient(i, factor))
}

/**
 * The batch in words, for the card's header: "40 portions · 20 kg".
 * The second half only when the recipe states what it makes.
 */
export function batchSummary(card: RecipeCard, factor: number): string {
  if (!(factor > 0)) return ''
  const base = round5(factor * card.base_qty)
  if (card.kind === 'prep') return formatQty(base, card.base_unit)
  const portions = `${Number.isInteger(base) ? base : round5(base)} ${unitLabel('portion', base)}`
  if (!canScaleByWeight(card)) return portions
  return `${portions} · ${formatQty(base * (card.portion_qty as number), card.portion_unit)}`
}

/** "Recipe written for 4 portions (2 kg)". */
export function writtenSummary(card: RecipeCard): string {
  const base = writtenAmount(card, 'base')
  if (card.kind === 'prep') return formatQty(base, card.base_unit)
  const portions = `${base} ${unitLabel('portion', base)}`
  if (!canScaleByWeight(card)) return portions
  return `${portions} (${formatQty(writtenAmount(card, 'weight'), card.portion_unit)})`
}

/** A quantity in one unit, restated in another of the same family; null when it cannot be. */
export function convertQty(qty: number, from: string, to: string): number | null {
  const v = convertUnits(qty, from, to)
  return v == null ? null : round5(v)
}

/** The query string of a dish card's options: "<id>:2,<id>". */
export function optionsQuery(options: { option_id: string; quantity?: number }[] | undefined): string {
  if (!options || options.length === 0) return ''
  return options
    .filter((o) => o.option_id)
    .map((o) => (o.quantity && o.quantity > 1 ? `${o.option_id}:${Math.floor(o.quantity)}` : o.option_id))
    .join(',')
}

/** A stable key for a target, for the query cache and the back stack. */
export function targetKey(t: RecipeCardTarget): string {
  switch (t.kind) {
    case 'order-item':
      return `order-item:${t.orderItemId}`
    case 'product':
      return `product:${t.productId}:${t.orderType ?? ''}:${optionsQuery(t.options)}`
    case 'prep':
      return `prep:${t.prepItemId}`
  }
}

/** The tenant's switch, read from the settings map. Anything unreadable is off. */
export function recipeCardsSwitchedOn(all: Record<string, unknown> | undefined): boolean {
  if (!all || all.kitchen_flow !== true) return false
  const v = all['kitchen_flow.recipe_cards']
  return typeof v === 'object' && v !== null && (v as { enabled?: unknown }).enabled === true
}
